import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { 
  FiSearch, FiFilter, FiExternalLink, FiMail, 
  FiMapPin, FiGlobe, FiPhone, FiFileText,
  FiRefreshCw, FiSend, FiPlus, FiX,
  FiChevronDown, FiChevronUp
} from 'react-icons/fi'
import { toast } from 'react-hot-toast'
import './Leads.css'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:3001/api'

const statusColors = {
  'new': 'status-accent',
  'contacted': 'status-info',
  'no_response': 'status-warning',
  'interested': 'status-success',
  'not_interested': 'status-danger',
  'follow_up': 'status-warning',
  'converted': 'status-success',
  'bad': 'status-danger'
}

const Leads = () => {
  const [statusFilter, setStatusFilter] = useState('')
  const [countyFilter, setCountyFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedLead, setExpandedLead] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editLead, setEditLead] = useState(null)
  const [showSendEmail, setShowSendEmail] = useState(null)

  const token = localStorage.getItem('token')

  const { data, isLoading, error, refetch } = useQuery(
    ['leads', statusFilter, countyFilter],
    async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (countyFilter) params.append('county', countyFilter)
      const response = await fetch(`${API_BASE}/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) throw new Error('Failed to fetch leads')
      return response.json()
    }
  )

  const { data: stats } = useQuery('lead-stats', async () => {
    const response = await fetch(`${API_BASE}/leads/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    if (!response.ok) throw new Error('Failed to fetch stats')
    return response.json()
  })

  const filteredLeads = data?.leads?.filter(lead => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return lead.business_name?.toLowerCase().includes(q) ||
             lead.county?.toLowerCase().includes(q) ||
             lead.location?.toLowerCase().includes(q) ||
             lead.email?.toLowerCase().includes(q)
    }
    return true
  }) || []

  const uniqueCounties = [...new Set(data?.leads?.map(l => l.county).filter(Boolean))].sort()

  const updateLeadStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/leads/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      })
      if (!response.ok) throw new Error('Update failed')
      toast.success(`Lead marked as ${newStatus}`)
      refetch()
    } catch (err) {
      toast.error('Failed to update lead')
    }
  }

  const handleSaveEdit = async (e) => {
    e.preventDefault()
    const form = e.target
    const updates = {}
    if (form.email.value) updates.email = form.email.value
    if (form.phone.value) updates.phone = form.phone.value
    if (form.notes.value) updates.notes = form.notes.value
    if (form.issue_identified.value) updates.issue_identified = form.issue_identified.value
    if (form.offering.value) updates.offering = form.offering.value
    if (form.website.value) updates.website = form.website.value

    try {
      const response = await fetch(`${API_BASE}/leads/${editLead.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      })
      if (!response.ok) throw new Error('Save failed')
      toast.success('Lead updated')
      setEditLead(null)
      refetch()
    } catch (err) {
      toast.error('Failed to save')
    }
  }

  return (
    <div className="leads-page">
      <div className="page-header">
        <div>
          <h1>Local Business Leads</h1>
          <p className="page-subtitle">
            Cold outreach targets across Ireland. Track contacts, website status, and follow-ups.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            <FiPlus /> Add Lead
          </button>
          <button className="btn-secondary" onClick={() => refetch()}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="lead-stats-row">
        <div className="lead-stat-card">
          <div className="lead-stat-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>
            {stats?.total || 0}
          </div>
          <div>
            <div className="lead-stat-value">{stats?.total || 0}</div>
            <div className="lead-stat-label">Total Leads</div>
          </div>
        </div>
        <div className="lead-stat-card">
          <div className="lead-stat-icon" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
            {stats?.contacted || 0}
          </div>
          <div>
            <div className="lead-stat-value">{stats?.contacted || 0}</div>
            <div className="lead-stat-label">Contacted</div>
          </div>
        </div>
        <div className="lead-stat-card">
          <div className="lead-stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
            {stats?.no_website || 0}
          </div>
          <div>
            <div className="lead-stat-value">{stats?.no_website || 0}</div>
            <div className="lead-stat-label">No Website</div>
          </div>
        </div>
        <div className="lead-stat-card">
          <div className="lead-stat-icon" style={{ background: 'rgba(6,182,212,0.15)', color: '#06b6d4' }}>
            {stats?.new_count || 0}
          </div>
          <div>
            <div className="lead-stat-value">{stats?.new_count || 0}</div>
            <div className="lead-stat-label">New (Not Contacted)</div>
          </div>
        </div>
      </div>

      {/* County breakdown */}
      {stats?.byCounty && (
        <div className="county-chips">
          {stats.byCounty.map(c => (
            <button
              key={c.county}
              className={`county-chip ${countyFilter === c.county ? 'active' : ''}`}
              onClick={() => setCountyFilter(countyFilter === c.county ? '' : c.county)}
            >
              <FiMapPin size={13} />
              {c.county} <span className="chip-count">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="lead-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search businesses, counties, emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FiX />
            </button>
          )}
        </div>
        <div className="status-filters">
          {['', 'new', 'contacted', 'interested', 'no_response', 'follow_up', 'converted', 'not_interested'].map(s => (
            <button
              key={s}
              className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s === statusFilter ? '' : s)}
            >
              {s ? s.replace('_', ' ') : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Leads list */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading leads...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <h3>Failed to load leads</h3>
          <p>Check backend connection</p>
          <button className="btn-secondary" onClick={() => refetch()}>Retry</button>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="empty-state">
          <FiGlobe size={48} />
          <h3>No leads found</h3>
          <p>{searchQuery ? 'Try different search terms' : 'Add your first business lead'}</p>
        </div>
      ) : (
        <div className="leads-list">
          {filteredLeads.map(lead => (
            <div key={lead.id} className="lead-card">
              <div className="lead-card-header">
                <div className="lead-title-row">
                  <div>
                    <h3 className="lead-title">{lead.business_name}</h3>
                    <div className="lead-meta">
                      {lead.county && (
                        <span className="lead-source">
                          <FiMapPin size={12} /> {lead.county}
                        </span>
                      )}
                      <span className={`opp-status ${statusColors[lead.status] || 'status-accent'}`}>
                        {lead.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  className="expand-btn"
                  onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                >
                  {expandedLead === lead.id ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>

              <div className="opp-body">
                <div className="lead-details">
                  {lead.website && (
                    <div className="lead-detail">
                      <FiGlobe className="detail-icon" />
                      <div>
                        <div className="detail-label">Website</div>
                        <a href={`https://${lead.website.startsWith('http') ? lead.website : lead.website}`} 
                           target="_blank" rel="noopener noreferrer" className="lead-link">
                          {lead.website} <FiExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                  )}
                  {lead.email && (
                    <div className="lead-detail">
                      <FiMail className="detail-icon" />
                      <div>
                        <div className="detail-label">Email</div>
                        <span className="lead-email">{lead.email}</span>
                      </div>
                    </div>
                  )}
                  {lead.phone && (
                    <div className="lead-detail">
                      <FiPhone className="detail-icon" />
                      <div>
                        <div className="detail-label">Phone</div>
                        <span>{lead.phone}</span>
                      </div>
                    </div>
                  )}
                  {lead.address && (
                    <div className="lead-detail">
                      <FiMapPin className="detail-icon" />
                      <div>
                        <div className="detail-label">Address</div>
                        <span>{lead.address}</span>
                      </div>
                    </div>
                  )}
                </div>

                {lead.issue_identified && (
                  <div className="lead-issue">
                    <strong>Issue: </strong>
                    {lead.issue_identified}
                  </div>
                )}

                {lead.offering && (
                  <div className="lead-offering">
                    <strong>Offer: </strong>
                    {lead.offering}
                  </div>
                )}

                {expandedLead === lead.id && (
                  <div className="lead-expanded">
                    {lead.notes && (
                      <div className="lead-notes">
                        <FiFileText size={14} style={{ flexShrink: 0 }} />
                        <span>{lead.notes}</span>
                      </div>
                    )}
                    <div className="lead-timing">
                      <span>Added: {new Date(lead.created_at).toLocaleDateString()}</span>
                      {lead.email_sent_at && (
                        <span>Email sent: {new Date(lead.email_sent_at).toLocaleDateString()}</span>
                      )}
                    </div>
                    <div className="lead-expanded-actions">
                      <select 
                        className="status-select"
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="interested">Interested</option>
                        <option value="no_response">No Response</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="converted">Converted</option>
                        <option value="not_interested">Not Interested</option>
                        <option value="bad">Bad Lead</option>
                      </select>
                      <button className="btn-secondary btn-sm" onClick={() => setEditLead(lead)}>
                        <FiFileText /> Edit
                      </button>
                      {lead.email && (
                        <a href={`mailto:${lead.email}?subject=${encodeURIComponent('Quick question about ' + lead.business_name + "'s website")}`} className="btn-primary btn-sm" target="_blank">
                          <FiSend /> Send Email
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {lead.email_sent_at && (
                <div className="opp-footer">
                  <span className="opp-agent">
                    <FiSend size={12} /> Emailed {new Date(lead.email_sent_at).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editLead && (
        <div className="modal-overlay" onClick={() => setEditLead(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Lead: {editLead.business_name}</h2>
              <button className="modal-close" onClick={() => setEditLead(null)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Website</label>
                  <input name="website" defaultValue={editLead.website || ''} placeholder="example.com" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input name="email" defaultValue={editLead.email || ''} placeholder="info@business.com" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" defaultValue={editLead.phone || ''} placeholder="+353 8X XXX XXXX" />
                </div>
                <div className="form-group">
                  <label>Issue Identified</label>
                  <input name="issue_identified" defaultValue={editLead.issue_identified || ''} placeholder="What's wrong with their current setup?" />
                </div>
                <div className="form-group">
                  <label>Offering</label>
                  <input name="offering" defaultValue={editLead.offering || ''} placeholder="e.g. Full website build + SEO" />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea name="notes" rows="4" defaultValue={editLead.notes || ''} placeholder="Additional notes..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setEditLead(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Lead</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault()
              const form = e.target
              const body = {
                business_name: form.business_name.value,
                website: form.website.value || null,
                email: form.email.value || null,
                phone: form.phone.value || null,
                address: form.address.value || null,
                county: form.county.value || null,
                category: form.category.value || null,
                issue_identified: form.issue_identified.value || null,
                notes: form.notes.value || null
              }
              try {
                const response = await fetch(`${API_BASE}/leads`, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify(body)
                })
                if (!response.ok) throw new Error('Failed')
                toast.success('Lead added!')
                setShowAddModal(false)
                refetch()
              } catch (err) {
                toast.error('Failed to add lead')
              }
            }}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Business Name *</label>
                  <input name="business_name" required placeholder="e.g. O'Brien's Bar & Restaurant" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Website</label>
                    <input name="website" placeholder="obriensbar.ie" />
                  </div>
                  <div className="form-group">
                    <label>County *</label>
                    <select name="county" required>
                      <option value="">Select county</option>
                      <option value="Louth">Louth</option>
                      <option value="Monaghan">Monaghan</option>
                      <option value="Cavan">Cavan</option>
                      <option value="Meath">Meath</option>
                      <option value="Dublin">Dublin</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input name="email" placeholder="info@business.ie" />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input name="phone" placeholder="+353 8X XXX XXXX" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input name="address" placeholder="Town, Co. County" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input name="category" placeholder="e.g. Accommodation, Retail, Hospitality" />
                </div>
                <div className="form-group">
                  <label>Issue Identified</label>
                  <input name="issue_identified" placeholder="e.g. No website, outdated design, no booking system" />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea name="notes" rows="3" placeholder="Any additional info..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Add Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Leads
